import { SubscriptionTier, UserRole } from '@/types/types';
import {
  Action,
  OrganizationPolicyData,
  OrganizationResource,
  OrganizationResourceData,
  PolicyCheck,
  WorkspacePolicyData,
  WorkspaceResource,
  WorkspaceResourceData,
} from './types';
import { ACCESS } from './utils';
import {
  MAX_KEYWORDS_PERMISSION_MESSAGE,
  MAX_ORGANIZATION_USERS_PERMISSION_MESSAGE,
  MAX_ORGANIZATION_WORKSPACES_PERMISSION_MESSAGE,
} from './consts';
import { connectorAttributeCheck } from './checks';

export const organizationRoles: Record<
  UserRole,
  Record<OrganizationResource, readonly Action[]>
> = {
  admin: {
    invitations: ACCESS.ALL,
    organization: ACCESS.ALL,
    users: ACCESS.ALL,
    workspaces: ACCESS.ALL,
  },
  editor: {
    invitations: ACCESS.VIEW,
    organization: ACCESS.VIEW,
    users: ACCESS.NONE,
    workspaces: ACCESS.NONE,
  },
  viewer: {
    organization: ACCESS.VIEW,
    users: ACCESS.NONE,
    invitations: ACCESS.NONE,
    workspaces: ACCESS.NONE,
  },
  dashboard_viewer: {
    users: ACCESS.NONE,
    invitations: ACCESS.NONE,
    organization: ACCESS.VIEW,
    workspaces: ACCESS.NONE,
  },
};

export const workspaceRoles: Record<
  UserRole,
  Record<WorkspaceResource, readonly Action[]>
> = {
  admin: {
    connectors: ACCESS.ALL,
    invitations: ACCESS.ALL,
    users: ACCESS.ALL,
    workspace: ACCESS.ALL,
    keyword: ACCESS.ALL,
    dashboard: ACCESS.ALL,
    connectorsList: ACCESS.ALL,
    connector: ACCESS.ALL,
  },
  editor: {
    connectors: ACCESS.ALL,
    invitations: ACCESS.NONE,
    users: ACCESS.NONE,
    workspace: ACCESS.VIEW,
    keyword: ACCESS.ALL,
    dashboard: ACCESS.VIEW,
    connectorsList: ACCESS.ALL,
    connector: ACCESS.ALL,
  },
  viewer: {
    connectors: ACCESS.VIEW,
    users: ACCESS.NONE,
    invitations: ACCESS.NONE,
    workspace: ACCESS.VIEW,
    keyword: ACCESS.VIEW,
    dashboard: ACCESS.VIEW,
    connectorsList: ACCESS.NONE,
    connector: ACCESS.VIEW,
  },
  dashboard_viewer: {
    connectors: ACCESS.NONE,
    users: ACCESS.NONE,
    invitations: ACCESS.NONE,
    workspace: ACCESS.VIEW,
    keyword: ACCESS.NONE,
    dashboard: ACCESS.VIEW,
    connectorsList: ACCESS.NONE,
    connector: ACCESS.NONE,
  },
};

export const subscriptionRoles: Record<
  SubscriptionTier,
  Record<WorkspaceResource | OrganizationResource, readonly Action[]>
> = {
  basic: {
    connectors: ACCESS.NONE,
    invitations: ACCESS.NONE,
    users: ACCESS.NONE,
    workspace: ACCESS.NONE,
    keyword: ACCESS.NONE,
    organization: ACCESS.VIEW,
    workspaces: ACCESS.NONE,
    dashboard: ACCESS.NONE,
    connectorsList: ACCESS.NONE,
    connector: ACCESS.NONE,
  },
  essentials: {
    connectors: ACCESS.ALL,
    invitations: ACCESS.ALL,
    users: ACCESS.ALL,
    workspace: ACCESS.ALL,
    keyword: ACCESS.ALL,
    organization: ACCESS.ALL,
    workspaces: ACCESS.ALL,
    dashboard: ACCESS.VIEW,
    connectorsList: ACCESS.ALL,
    connector: ACCESS.ALL,
  },
  advanced: {
    connectors: ACCESS.ALL,
    invitations: ACCESS.ALL,
    users: ACCESS.ALL,
    workspace: ACCESS.ALL,
    keyword: ACCESS.ALL,
    organization: ACCESS.ALL,
    workspaces: ACCESS.ALL,
    dashboard: ACCESS.VIEW,
    connectorsList: ACCESS.ALL,
    connector: ACCESS.ALL,
  },
};

export const workspacePolicies: PolicyCheck<WorkspaceResourceData, WorkspacePolicyData> =
  {
    connector: {
      create: connectorAttributeCheck,
      update: connectorAttributeCheck,
    },
    keyword: {
      create: (_, constraints, resource) => {
        const maxKeywords = parseInt(constraints.dataforseo_max_keywords);
        const newKeywordsNum =
          resource.numOfAddingKeywords + resource.numOfExistingKeywords;
        return newKeywordsNum <= maxKeywords
          ? true
          : MAX_KEYWORDS_PERMISSION_MESSAGE(maxKeywords, newKeywordsNum);
      },
    },
  } as const;

export const organizationPolicies: PolicyCheck<
  OrganizationResourceData,
  OrganizationPolicyData
> = {
  invitations: {
    create(_, constraints, resource) {
      const maxUsers = parseInt(constraints.organization_max_users);
      return resource.currentInvitationsNum +
        resource.currentUsersNum +
        resource.newInvitationsNum <=
        maxUsers
        ? true
        : MAX_ORGANIZATION_USERS_PERMISSION_MESSAGE(maxUsers);
    },
  },
  workspaces: {
    create: (policyData, constraints) => {
      const maxWorkspaces = parseInt(constraints.organization_max_workspaces);
      return policyData.selectedOrganization.workspaces.length < maxWorkspaces
        ? true
        : MAX_ORGANIZATION_WORKSPACES_PERMISSION_MESSAGE(maxWorkspaces);
    },
  },
} as const;
